// Dashboard IA de LisualPro (inspirado en el de Enlaps/myTikee, con métricas de obra propias).
// Un filtro global (período + clases + ajustes) maneja: gráfico, indicadores, calendario, visor y mapa de calor.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FAMILIES, ALL_CLASSES, classInfo, DEFAULT_CFG, loadDays, listDays, dayKey, dayStartMs, addDays, todayKey,
  inWork, aggregate, fillGaps, allowedGrans, autoGran, GRANS, fmtDMY, fmtHM, fmtKey, bucketLabel, loadHelmetFixes,
} from './iaData';
import DiagnosisPanel from './DiagnosisPanel';
import { resolveLocation, loadWeather, weatherForRows, WX_KINDS, WX_ORDER } from './weather';
import { UbicacionObraModal, CambiarUbicacionLink } from '../../../obras/components/UbicacionObra';
import HelmetReview from './HelmetReview';
import ShareModal from './ShareModal';
import TimeChart from './TimeChart';
import { WX_ICONS, skyCss } from './clima/Sky';
import KeyIndicators, { ActivityCalendar } from './KeyIndicators';
import DetectionsPanel from './DetectionsPanel';
import HeatmapPanel from './HeatmapPanel';
import SensitivityModal from './SensitivityModal';
import { getZonasSeguro, tieneZonas } from './zonas';
import { T, Card, Chip, Btn, Seg, inputStyle } from './ui';

const PRESETS = [
  { value: 'hoy', label: 'Hoy', days: 1, offset: 0 },
  { value: 'ayer', label: 'Ayer', days: 1, offset: 1 },
  { value: '7', label: '7 días', days: 7 },
  { value: '14', label: '14 días', days: 14 },
  { value: '30', label: '30 días', days: 30 },
  { value: '90', label: '90 días', days: 90 },
];
const presetPeriod = (p) => {
  const end = addDays(todayKey(), -(p.offset || 0));
  return { from: dayStartMs(addDays(end, -p.days + 1)), to: dayStartMs(addDays(end, 1)) };
};
const DEFAULT_PRESET = PRESETS[3];
// Rango propio de la sección Detecciones (desde – hasta). Tope de días para que cargue rápido.
const DET_PRESETS = PRESETS.filter((p) => ['hoy', 'ayer', '7', '30'].includes(p.value));
const DET_MAX_DAYS = 90;

const loadCfg = (cam) => {
  try { return { ...DEFAULT_CFG, ...JSON.parse(localStorage.getItem('iaDash:' + cam) || '{}') }; } catch (e) { return DEFAULT_CFG; }
};

export default function IADashboard({ customerId, camIdentifier, camName, coords, feedId, onEditZonas }) {
  const [cfgState, setCfg] = useState(() => loadCfg(camIdentifier));
  useEffect(() => { try { localStorage.setItem('iaDash:' + camIdentifier, JSON.stringify(cfgState)); } catch (e) { /* sin storage */ } }, [cfgState, camIdentifier]);
  // Correcciones de casco del cliente: cada cambio recalcula todo el panel
  useMemo(() => loadHelmetFixes(camIdentifier), [camIdentifier]);
  const [fixVer, setFixVer] = useState(0);
  const cfg = useMemo(() => ({ ...cfgState, _fx: fixVer }), [cfgState, fixVer]);
  const [showReview, setShowReview] = useState(false);
  const [showShare, setShowShare] = useState(false);
  // Modo interno (equipo Lisual): ?interno=1 una vez y queda guardado; ?interno=0 lo apaga.
  // Muestra la confianza del modelo y las herramientas para corregir y reentrenar. El cliente no lo ve.
  const internal = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get('interno');
    try {
      if (q === '1') localStorage.setItem('lisualInterno', '1');
      if (q === '0') localStorage.removeItem('lisualInterno');
      return localStorage.getItem('lisualInterno') === '1';
    } catch (e) { return q === '1'; }
  }, []);

  const [period, setPeriod] = useState(() => presetPeriod(DEFAULT_PRESET));
  const [preset, setPreset] = useState(DEFAULT_PRESET.value);
  const [photos, setPhotos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [granUser, setGranUser] = useState(null);
  const [series, setSeries] = useState({ det: true, act: true, casco: true, finde: true, clima: true });
  // Clima: ubicación de la obra + horas de Open-Meteo del período
  const [loc, setLoc] = useState(undefined); // undefined = sin buscar, null = no hay
  const [hours, setHours] = useState(null);
  const [climaErr, setClimaErr] = useState(null);
  const [climaLoading, setClimaLoading] = useState(false);
  const [climaRetry, setClimaRetry] = useState(0);
  const [cambiarUbi, setCambiarUbi] = useState(false);
  const [chartType, setChartType] = useState('barras');
  const [focus, setFocus] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [showClasses, setShowClasses] = useState(false);
  const detRef = useRef(null);

  // Zonas de detección de la cámara: lo que el cliente marcó como fuera de interés no se cuenta en nada
  const [zonas, setZonas] = useState(undefined); // undefined = cargando
  useEffect(() => { let alive = true; getZonasSeguro(feedId).then((z) => alive && setZonas(z)); return () => { alive = false; }; }, [feedId]);

  // Carga los días que cubren el período (mismo endpoint que la pestaña IA)
  useEffect(() => {
    if (zonas === undefined) return undefined;
    let alive = true;
    const days = listDays(dayKey(period.from), dayKey(period.to - 1));
    setProgress({ done: 0, total: days.length });
    loadDays(camIdentifier, customerId, days, (done, total, partial) => { if (!alive) return; setProgress({ done, total }); if (partial) setPhotos(partial); }, { zonas }).then((ph) => {
      if (!alive) return;
      setPhotos(ph);
      setProgress(null);
    });
    return () => { alive = false; };
  }, [camIdentifier, customerId, period, zonas]);

  const inPeriod = useMemo(() => photos.filter((p) => p.ms >= period.from && p.ms < period.to && inWork(p.ms, cfg)), [photos, period, cfg]);
  const grans = useMemo(() => allowedGrans(inPeriod, period.from, period.to), [inPeriod, period]);
  const gran = granUser && grans.includes(granUser) ? granUser : autoGran(inPeriod, period.from, period.to);
  // Los intervalos sin fotos analizadas también van (vacíos): el período se ve entero y el clima de esos días también
  const rowsBase = useMemo(() => fillGaps(aggregate(inPeriod, cfg, gran), gran, period.from, Math.min(period.to, Date.now()), cfg), [inPeriod, cfg, gran, period]);
  useEffect(() => {
    if (!series.clima || loc !== undefined) return;
    resolveLocation({ coords, feedId, cam: camIdentifier }).then(setLoc);
  }, [series.clima, loc, coords, feedId, camIdentifier]);
  useEffect(() => {
    if (!series.clima || !loc) return undefined;
    let alive = true;
    setClimaErr(null);
    setClimaLoading(true);
    loadWeather(loc, dayKey(period.from), dayKey(period.to - 1))
      .then((h) => { if (alive) { setHours(h); setClimaLoading(false); } })
      .catch(() => { if (alive) { setClimaErr('El servicio del clima no respondió.'); setClimaLoading(false); } });
    return () => { alive = false; };
  }, [series.clima, loc, period, climaRetry]);
  const rows = useMemo(() => (series.clima && hours ? weatherForRows(rowsBase, gran, hours) : rowsBase), [rowsBase, gran, hours, series.clima]);
  const wxPresent = useMemo(() => WX_ORDER.filter((k) => rows.some((r) => r.wx && r.wx.kind === k)), [rows]);
  const photoAt = useMemo(() => new Map(inPeriod.map((p) => [p.ms, p])), [inPeriod]);
  const viewPhotos = useMemo(() => (focus ? inPeriod.filter((p) => p.ms >= focus.from && p.ms < focus.to) : inPeriod), [inPeriod, focus]);

  const setP = (p, key = null) => { setPeriod(p); setPreset(key); setGranUser(null); setFocus(null); setDetSel(null); };
  const reset = () => setP(presetPeriod(DEFAULT_PRESET), DEFAULT_PRESET.value);
  const periodLabel = `${fmtDMY(period.from)}${period.to - period.from > 864e5 ? ' → ' + fmtDMY(period.to - 1) : ''}`;
  const viewLabel = focus ? focus.label : periodLabel;

  // Detecciones: por defecto siguen al gráfico (período o la barra elegida); el cliente puede elegir otro rango
  const [detSel, setDetSel] = useState(null); // null = igual que el gráfico · { from, to, preset }
  const [detPhotos, setDetPhotos] = useState(null);
  const [detProgress, setDetProgress] = useState(null);
  const detInsidePeriod = !!detSel && detSel.from >= period.from && detSel.to <= period.to;
  useEffect(() => {
    if (!detSel || detInsidePeriod || zonas === undefined) { setDetPhotos(null); setDetProgress(null); return undefined; }
    let alive = true;
    const days = listDays(dayKey(detSel.from), dayKey(detSel.to - 1));
    setDetPhotos(null);
    setDetProgress({ done: 0, total: days.length });
    loadDays(camIdentifier, customerId, days, (done, total) => alive && setDetProgress({ done, total }), { zonas }).then((ph) => {
      if (!alive) return;
      setDetPhotos(ph);
      setDetProgress(null);
    });
    return () => { alive = false; };
  }, [detSel, detInsidePeriod, camIdentifier, customerId, zonas]);
  const detView = useMemo(() => {
    if (!detSel) return viewPhotos;
    const src = detInsidePeriod ? photos : detPhotos || [];
    return src.filter((p) => p.ms >= detSel.from && p.ms < detSel.to && inWork(p.ms, cfg));
  }, [detSel, detInsidePeriod, photos, detPhotos, viewPhotos, cfg]);
  const detLoading = detSel ? (detInsidePeriod ? progress : detProgress) : progress;
  const detLabel = detSel ? `${fmtDMY(detSel.from)}${detSel.to - detSel.from > 864e5 ? ' → ' + fmtDMY(detSel.to - 1) : ''}` : viewLabel;
  const pickDet = (fromKey, toKey, key = null) => {
    let a = fromKey <= toKey ? fromKey : toKey;
    const b = fromKey <= toKey ? toKey : fromKey;
    if (listDays(a, b).length > DET_MAX_DAYS) a = addDays(b, -(DET_MAX_DAYS - 1));
    setDetSel({ from: dayStartMs(a), to: dayStartMs(addDays(b, 1)), preset: key });
  };
  const detFromKey = dayKey(detSel ? detSel.from : focus ? focus.from : period.from);
  const detToKey = dayKey((detSel ? detSel.to : focus ? focus.to : period.to) - 1);
  const emptyPeriod = !progress && !inPeriod.length;

  const toggleClass = (c) => setCfg({ ...cfgState, enabled: cfg.enabled.includes(c) ? cfg.enabled.filter((x) => x !== c) : [...cfg.enabled, c] });
  const toggleFamily = (f) => {
    const allOn = f.classes.every((c) => cfg.enabled.includes(c));
    setCfg({ ...cfgState, enabled: allOn ? cfg.enabled.filter((c) => !f.classes.includes(c)) : [...new Set([...cfg.enabled, ...f.classes])] });
  };
  const enabledList = ALL_CLASSES.filter((c) => cfg.enabled.includes(c));

  return (
    <div style={{ background: T.bg, color: T.text, borderRadius: 24, padding: 'clamp(12px, 2vw, 22px)' }}>
      <style>{`
        .iad-chart svg:focus,.iad-chart .recharts-wrapper:focus,.iad-chart *:focus{outline:none}
        @media print{.iad-noprint{display:none!important}}
        .iad-top{display:grid;grid-template-columns:minmax(0,2fr) minmax(280px,1fr);gap:16px}
        @media (max-width:1100px){.iad-top{grid-template-columns:1fr}}
        @media (max-width:760px){.iad-det-grid,.iad-share-grid{grid-template-columns:1fr!important}}
      `}</style>

      {/* Encabezado */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Dashboard IA <span style={{ fontWeight: 400, fontStyle: 'italic', color: T.muted }}>{camName || camIdentifier}</span></div>
          <div style={{ fontSize: 12, color: T.muted }}>Personas, maquinaria y vehículos detectados en cada foto de la cámara</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ background: T.blue + '12', color: T.blue, border: `1px solid ${T.blue}44`, borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 600 }}>
            {progress ? `Cargando ${progress.done} de ${progress.total} días…` : `${inPeriod.length.toLocaleString('es-AR')} fotos analizadas`}
          </span>
          {tieneZonas(zonas) && (
            <span title="Las detecciones fuera de las zonas elegidas no se cuentan en gráficos, indicadores, reportes ni alertas" style={{ background: T.green + '14', color: '#047857', border: `1px solid ${T.green}55`, borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 600, display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              Zonas de detección activas
              {onEditZonas && <button type="button" onClick={onEditZonas} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, textDecoration: 'underline' }}>Editar</button>}
            </span>
          )}
          {internal && <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>Modo interno Lisual</span>}
          <Btn active disabled={!!progress} onClick={() => setShowShare(true)} title="Mandar el informe por mail, PDF o WhatsApp">📤 Compartir informe</Btn>
          <Btn onClick={() => setShowCfg(true)} title="Qué mostrar y jornada de la obra">⚙ Ajustes</Btn>
        </div>
      </div>

      <div className="iad-top">
        {/* Gráfico */}
        <Card
          title={<>
            Gráfico
            <Chip color={T.blue} on={series.det} onClick={() => setSeries({ ...series, det: !series.det })}>Detecciones {series.det ? '✓' : ''}</Chip>
            <Chip color={T.green} on={series.act} onClick={() => setSeries({ ...series, act: !series.act })}>Actividad {series.act ? '✓' : ''}</Chip>
            <Chip color={T.yellow} on={series.casco} onClick={() => setSeries({ ...series, casco: !series.casco })}>% Casco {series.casco ? '✓' : ''}</Chip>
            <Chip color="#0284c7" on={series.clima} onClick={() => setSeries({ ...series, clima: !series.clima })} title="Cómo estuvo el tiempo: ícono y color de fondo por día u hora, lluvia (celeste) y temperatura (línea punteada)">{series.clima ? '☑' : '☐'} Clima</Chip>
            <Chip color="#64748b" on={series.finde} onClick={() => setSeries({ ...series, finde: !series.finde })} title="Sombrea sábados y domingos">{series.finde ? '☑' : '☐'} Fines de semana</Chip>
          </>}
          right={<select value={chartType} onChange={(e) => setChartType(e.target.value)} style={inputStyle} title="Tipo de gráfico">
            <option value="barras">Barras apiladas</option><option value="areas">Áreas apiladas</option><option value="lineas">Líneas</option>
          </select>}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, position: 'relative' }}>
            <button type="button" onClick={() => setShowClasses(!showClasses)} style={{ ...inputStyle, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer', maxWidth: '100%' }}>
              {enabledList.slice(0, 3).map((c) => <span key={c} style={{ color: classInfo(c).color, fontWeight: 600 }}>{classInfo(c).label}</span>)}
              {enabledList.length > 3 && <span style={{ color: T.muted }}>+{enabledList.length - 3}</span>}
              {!enabledList.length && <span style={{ color: T.red }}>Elegí al menos una clase</span>}
              <span style={{ color: T.blue }}>{showClasses ? '▴' : '▾'}</span>
            </button>
            {showClasses && (
              <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: T.card2, border: `1px solid ${T.line}`, borderRadius: 12, padding: 14, display: 'flex', gap: 22, flexWrap: 'wrap', boxShadow: '0 12px 30px rgba(0,0,0,.12)' }}>
                {FAMILIES.map((f) => (
                  <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button type="button" onClick={() => toggleFamily(f)} style={{ background: 'none', border: 'none', color: T.text, fontWeight: 700, fontSize: 13, textAlign: 'left', cursor: 'pointer', padding: 0 }}>{f.label}</button>
                    {f.classes.map((c) => <Chip key={c} small color={classInfo(c).color} on={cfg.enabled.includes(c)} onClick={() => toggleClass(c)}>{classInfo(c).label}</Chip>)}
                  </div>
                ))}
              </div>
            )}
            <Seg value={gran} onChange={setGranUser} options={GRANS.map((g) => ({ value: g.key, label: g.label, disabled: !grans.includes(g.key) }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <Seg value={preset} onChange={(v) => setP(presetPeriod(PRESETS.find((p) => p.value === v)), v)} options={PRESETS} />
            <input type="date" style={inputStyle} value={dayKey(period.from)} max={todayKey()} onChange={(e) => e.target.value && setP({ from: dayStartMs(e.target.value), to: Math.max(period.to, dayStartMs(addDays(e.target.value, 1))) })} />
            <span style={{ color: T.muted }}>→</span>
            <input type="date" style={inputStyle} value={dayKey(period.to - 1)} max={todayKey()} onChange={(e) => e.target.value && setP({ from: Math.min(period.from, dayStartMs(e.target.value)), to: dayStartMs(addDays(e.target.value, 1)) })} />
            <Btn onClick={reset} title="Volver a los últimos 14 días (o doble clic en el gráfico)">⟲</Btn>
          </div>

          {progress && (
            <div style={{ height: 4, background: T.line, borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ width: `${(100 * progress.done) / Math.max(1, progress.total)}%`, height: '100%', background: T.blue, transition: 'width .2s' }} />
            </div>
          )}
          {series.clima && loc === null && <SinUbicacion onClick={() => setCambiarUbi(true)} />}
          <UbicacionObraModal open={cambiarUbi} onClose={() => setCambiarUbi(false)} feedId={feedId} cam={camIdentifier}
            initial={loc || null} direccion={loc?.direccion || ''} onSaved={(l) => { setCambiarUbi(false); setHours(null); setClimaErr(null); setLoc(l); }} />
          {series.clima && loc && (
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 6, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {wxPresent.map((k) => (
                <span key={k} title={WX_KINDS[k].label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${T.line}`, borderRadius: 999, padding: '2px 10px 2px 3px', color: T.text, fontWeight: 500, boxShadow: '0 1px 2px rgba(15,23,42,.05)' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 999, background: skyCss(k), display: 'grid', placeItems: 'center' }}>
                    <img src={WX_ICONS[k]} alt="" width={18} height={18} />
                  </span>
                  {WX_KINDS[k].label}
                </span>
              ))}
              {!wxPresent.length && hours && ['semana', 'mes'].includes(gran) && <span>Los íconos del tiempo se ven por día o por hora.</span>}
              <span style={{ color: T.muted }}><span style={{ color: '#0284c7' }}>▬ lluvia (mm)</span> · <span style={{ color: '#e11d48' }}>┄ temperatura</span> · {loc.label}</span>
              {climaLoading && <span>Cargando el clima…</span>}
              {climaErr && <span style={{ color: T.red }}>{climaErr} <button type="button" onClick={() => setClimaRetry((n) => n + 1)} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0, textDecoration: 'underline' }}>Reintentar</button></span>}
              {feedId && <CambiarUbicacionLink onClick={() => setCambiarUbi(true)} />}
            </div>
          )}
          {emptyPeriod && (
            <div style={{ background: '#f8fafc', border: `1px solid ${T.line}`, borderRadius: 12, padding: '8px 12px', marginBottom: 8, fontSize: 13, color: T.muted }}>
              No hay fotos analizadas por la IA entre el {fmtKey(dayKey(period.from))} y el {fmtKey(dayKey(period.to - 1))}{series.clima && loc ? '. Igual te mostramos cómo estuvo el tiempo.' : '.'}
            </div>
          )}
          {rows.length ? (
            <TimeChart rows={rows} gran={gran} cfg={cfg} series={series} chartType={chartType} photoAt={photoAt}
              onZoom={(a, b) => setP({ from: a, to: b })} onReset={reset}
              onBucket={(r) => {
                setFocus({ from: r.start, to: r.end > r.start ? r.end : r.start + 1, label: `${GRANS.find((g) => g.key === gran).label} ${bucketLabel(r.start, gran)}` });
                setDetSel(null);
                setTimeout(() => detRef.current && detRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
              }} />
          ) : (
            <div style={{ height: 340, display: 'grid', placeItems: 'center', color: T.muted }}>
              {progress ? `Cargando ${progress.done} de ${progress.total} días…` : 'No hay fotos analizadas en este período.'}
            </div>
          )}
          <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
            Cada barra = promedio por foto en {gran === 'foto' ? 'esa foto' : `ese ${GRANS.find((g) => g.key === gran).label.toLowerCase()}`}. Arrastrá sobre el gráfico para acercar, doble clic para volver, clic en una barra para ver sus fotos.
          </div>
        </Card>

        {/* Indicadores clave */}
        <Card title="Indicadores clave">
          <KeyIndicators cam={camIdentifier} feedId={feedId} customerId={customerId} cfg={cfg} endKey={dayKey(period.to - 1)} ready={!progress} />
        </Card>
      </div>

      <Card style={{ marginTop: 16 }}
        title={<>Diagnóstico de la obra <span style={{ fontWeight: 400, fontSize: 12, color: T.muted }}>{viewLabel} · qué está pasando y qué hacer</span></>}>
        {progress ? <Wait p={progress} /> : <DiagnosisPanel photos={viewPhotos} cfg={cfg} fromKey={dayKey(focus ? focus.from : period.from)} toKey={dayKey((focus ? focus.to : period.to) - 1)}
          name={camName || camIdentifier} rangeLabel={viewLabel} onReview={() => setShowReview(true)} internal={internal} />}
      </Card>

      <Card title={<>Calendario de obra <span style={{ fontWeight: 400, fontSize: 12, color: T.muted }}>{periodLabel}</span></>} style={{ marginTop: 16 }}>
        {progress ? <Wait p={progress} /> : <ActivityCalendar photos={inPeriod} cfg={cfg} fromKey={dayKey(period.from)} toKey={dayKey(period.to - 1)}
          onDay={(k) => setP({ from: dayStartMs(k), to: dayStartMs(addDays(k, 1)) })} />}
      </Card>

      <div ref={detRef} style={{ scrollMarginTop: 80 }}>
        <Card style={{ marginTop: 16 }}
          title={<>Detecciones <Chip color={detSel ? T.blue : focus ? T.yellow : T.blue}>{detLabel}</Chip>
            {!detSel && focus && <Chip color={T.muted} on={false} onClick={() => setFocus(null)}>✕ ver todo el período</Chip>}
            {detSel && <Chip color={T.muted} on={false} onClick={() => setDetSel(null)} title="Volver a las fechas del gráfico">✕ usar las fechas del gráfico</Chip>}</>}
          right={<span style={{ fontSize: 12, color: T.muted }}>Rueda del mouse = zoom · clic en un recorte = acercar</span>}>
          <div className="iad-noprint" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <Seg value={detSel ? detSel.preset : null} onChange={(v) => { const pr = presetPeriod(PRESETS.find((p) => p.value === v)); pickDet(dayKey(pr.from), dayKey(pr.to - 1), v); }} options={DET_PRESETS} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.muted }}>
              Desde
              <input type="date" aria-label="Detecciones desde" style={inputStyle} value={detFromKey} max={todayKey()}
                onChange={(e) => e.target.value && pickDet(e.target.value, e.target.value > detToKey ? e.target.value : detToKey)} />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.muted }}>
              hasta
              <input type="date" aria-label="Detecciones hasta" style={inputStyle} value={detToKey} max={todayKey()}
                onChange={(e) => e.target.value && pickDet(e.target.value < detFromKey ? e.target.value : detFromKey, e.target.value)} />
            </label>
            <span style={{ fontSize: 11, color: T.muted }}>Hasta {DET_MAX_DAYS} días</span>
          </div>
          {detLoading ? <Wait p={detLoading} /> : <DetectionsPanel photos={detView} cfg={cfg} onFix={() => setFixVer((v) => v + 1)} onReview={() => setShowReview(true)} internal={internal} />}
        </Card>
      </div>

      <Card style={{ marginTop: 16 }} title={<>Mapa de calor <Chip color={focus ? T.yellow : T.blue}>{viewLabel}</Chip></>}>
        {progress ? <Wait p={progress} /> : <HeatmapPanel photos={viewPhotos} cfg={cfg} rangeLabel={viewLabel} />}
      </Card>

      <p style={{ fontSize: 11, color: T.muted, marginTop: 14, lineHeight: 1.5 }}>
        Datos obtenidos de las fotos de la cámara de la obra (aprox. una cada 10 minutos). Hora de obra (Argentina). {cfg.onlyWork ? `Se cuentan solo las fotos de ${cfg.workFrom} a ${cfg.workTo} h.` : 'Se cuentan las fotos de todo el día.'}
        {inPeriod.length ? ` Última foto: ${fmtDMY(inPeriod[inPeriod.length - 1].ms)} ${fmtHM(inPeriod[inPeriod.length - 1].ms)}.` : ''}
      </p>

      {showCfg && <SensitivityModal cfg={cfgState} onApply={setCfg} onClose={() => setShowCfg(false)} internal={internal} />}
      {showShare && <ShareModal cam={camIdentifier} feedId={feedId} customerId={customerId} cfg={cfg} name={camName || camIdentifier} period={focus ? { from: focus.from, to: focus.to } : period} onClose={() => setShowShare(false)} />}
      {internal && showReview && <HelmetReview photos={viewPhotos} cfg={cfg} cam={camIdentifier} onChange={() => setFixVer((v) => v + 1)} onClose={() => setShowReview(false)} />}
    </div>
  );
}

function Wait({ p }) {
  return <div style={{ color: T.muted, fontSize: 13, padding: '18px 4px' }}>Cargando {p.done} de {p.total} días…</div>;
}

function SinUbicacion({ onClick }) {
  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '10px 12px', marginBottom: 10, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ flex: 1, minWidth: 200 }}><b>¿Dónde está tu obra?</b> Ubicala en el mapa y sumamos la lluvia y la temperatura al gráfico.</span>
      <Btn active onClick={onClick}>📍 Ubicar obra</Btn>
    </div>
  );
}
